/**
 * A flight recorder for opening an experience or a task, on in development only.
 *
 * "Experience not found" and "Task not found" were both reported for an experience whose every
 * request returned 200 and whose ten tasks were all present, unforked and undeleted. Nothing in the
 * server log, the database or the source could say what failed, because the page discarded the
 * cause: one `.catch()` covered the fetch and everything done with its result, and printed a
 * sentence about missing data for either.
 *
 * The failure is also intermittent — a few laps of open/rename/back/open and then it stops — which
 * is exactly the kind that a trace added on purpose never sees. So this stays in, and survives a
 * reload, so the lap that went wrong can be read back afterwards instead of being reproduced on
 * demand.
 *
 * What it records is the path from a click to a rendered screen:
 *
 *   - `mount`   — a page mounting, and the ids it was given by the route;
 *   - `load`    — the fetch starting, settling, or failing, and on which side of the `await`;
 *   - `resolve` — the task looked for, the ids that came back, and whether it was among them;
 *   - `screen`  — what the page actually showed, and why it showed that.
 *
 * Read it with `__load.dump()` in the console, or `copy(__load.json())`. `__load.clear()` starts a
 * fresh recording. Capped, so a long session costs a bounded amount of storage and the oldest
 * entries fall off rather than the newest being dropped.
 */

/** Entries kept. Each is small; this is a few hundred page opens. */
const CAP = 1000;
const KEY = 'agavi.loadLog';
/** Task-id lists are the bulky field, and the first handful answer the question on their own. */
const MAX_IDS = 40;

export type LoadEventKind = 'mount' | 'load' | 'resolve' | 'screen' | 'note';

export interface LoadEvent {
  /** Milliseconds since the page loaded, so a sequence can be read as timing. */
  t: number;
  kind: LoadEventKind;
  [field: string]: unknown;
}

/** Off outside development: the calls stay, the work does not happen. */
const ON = import.meta.env.DEV;

let buffer: LoadEvent[] = [];
let loaded = false;
/** Writes are batched to an idle moment — a recorder that stalls navigation is worse than none. */
let flushHandle: ReturnType<typeof setTimeout> | null = null;

function load(): void {
  if (loaded) return;
  loaded = true;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) buffer = JSON.parse(raw) as LoadEvent[];
  } catch { /* private window, blocked storage, corrupt JSON — an empty log is fine */ }
}

function flushNow(): void {
  if (flushHandle) { clearTimeout(flushHandle); flushHandle = null; }
  try { localStorage.setItem(KEY, JSON.stringify(buffer)); }
  catch { /* over quota or blocked: keep recording in memory */ }
}

/**
 * Batched to an idle moment, because a recorder that stalls navigation is worse than none.
 *
 * With one exception, and it was the hole that made this recorder useless for the failure it was
 * built for: a 400ms delay is longer than it takes to read an error screen and hit reload, so the
 * entry describing the failure was still in memory when the page went away, and the log came back
 * empty every time. An event carrying an `error` flushes SYNCHRONOUSLY — at most one write per
 * failed lap, on a lap that has already gone wrong, which is the cheapest possible moment to spend
 * a millisecond.
 */
function scheduleFlush(immediate: boolean): void {
  if (immediate) { flushNow(); return; }
  if (flushHandle) return;
  flushHandle = setTimeout(() => { flushHandle = null; flushNow(); }, 400);
}

/**
 * An error reduced to something that survives `JSON.stringify`.
 *
 * A thrown `Error` serializes to `{}`, which is how the original cause came to be lost in the first
 * place — so name, message, any HTTP status and the first few stack frames are pulled out by hand.
 */
export function describeError(error: unknown): Record<string, unknown> {
  if (!(error instanceof Error)) return { thrown: String(error) };
  // `status` is read off the value rather than through `ApiError`, so a rejection from anywhere
  // that carries one is described the same way — including one this module has never heard of.
  const status = (error as unknown as { status?: unknown }).status;
  return {
    name: error.name,
    message: error.message,
    ...(typeof status === 'number' ? { status } : {}),
    stack: error.stack?.split('\n').slice(0, 6).join('\n'),
  };
}

/** Trim an id list to something a log can hold, keeping the count honest. */
export function sampleIds(ids: string[]): { count: number; ids: string[] } {
  return { count: ids.length, ids: ids.slice(0, MAX_IDS) };
}

/**
 * Record one event.
 *
 * Deliberately forgiving about its payload: this is a diagnostic, and a recorder that throws while
 * describing a bug would take the bug's own evidence with it.
 */
export function recordLoadEvent(kind: LoadEventKind, fields: Record<string, unknown>): void {
  if (!ON) return;
  try {
    load();
    buffer.push({ t: Math.round(performance.now()), kind, url: location.pathname + location.search, ...fields });
    if (buffer.length > CAP) buffer.splice(0, buffer.length - CAP);
    // Both ends of a failed lap: the CAUSE (`error`) and the OUTCOME (`showing: 'error'`). The
    // outcome is the one a teacher is looking at when they reach for reload, so it is the entry
    // most likely to be lost and the one most worth not losing.
    scheduleFlush('error' in fields || fields.showing === 'error');
  } catch { /* never let the recorder break the thing it is recording */ }
}

/** Everything recorded, oldest first. */
export function loadLog(): LoadEvent[] {
  if (!ON) return [];
  load();
  return [...buffer];
}

export function clearLoadLog(): void {
  buffer = [];
  loaded = true;
  try { localStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}

/**
 * Install the console handle.
 *
 * Called once from the page. Kept off `window` in production builds entirely, rather than present
 * and empty, so nothing can come to depend on it.
 */
export function installLoadRecorder(): void {
  if (!ON) return;
  load();
  /*
   * The last thing written before the page goes.
   *
   * `pagehide` fires on a reload, a navigation and a tab close, and unlike `beforeunload` it also
   * fires when the page goes into the back/forward cache — so a batched entry is on disk before
   * anything can take it with it. `visibilitychange` catches the case `pagehide` misses on mobile,
   * where a backgrounded tab can be discarded without ever hiding.
   */
  window.addEventListener('pagehide', flushNow);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushNow(); });
  (window as unknown as { __load: unknown }).__load = {
    /** The whole log as a table, newest last. */
    dump: (kind?: LoadEventKind) => {
      const rows = kind ? loadLog().filter(e => e.kind === kind) : loadLog();
      // eslint-disable-next-line no-console
      console.table(rows);
      return `${rows.length} events`;
    },
    /**
     * Just the laps that ended on an error screen, with everything recorded since the mount that
     * led to each — which is the whole question when the failure is one lap in ten.
     */
    failures: () => {
      const all = loadLog();
      return all
        .map((e, i) => [e, i] as const)
        .filter(([e]) => e.kind === 'screen' && e.showing === 'error')
        .map(([, i]) => {
          const start = all.slice(0, i).findLastIndex((e) => e.kind === 'mount');
          return all.slice(start === -1 ? 0 : start, i + 1);
        });
    },
    /** For handing the recording to someone else — `copy(__load.json())`. */
    json: () => JSON.stringify(loadLog(), null, 1),
    events: loadLog,
    clear: () => { clearLoadLog(); return 'load log cleared'; },
  };
}
